/**
 * contentStorage.js — Save/load generated content from Railway Postgres
 * Replaces localStorage for findings and ideas
 */
import { MEDIA_STUDIO_API_KEY } from '../constants';

const API = 'https://strategic-honesty-media-studio-production.up.railway.app';

const getHeaders = () => ({
  'Content-Type': 'application/json',
  ...(MEDIA_STUDIO_API_KEY ? { 'x-api-key': MEDIA_STUDIO_API_KEY } : {}),
});

export async function saveContent(type, items) {
  if (!items?.length) return;
  try {
    await fetch(`${API}/api/content`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ type, items }),
    });
  } catch (e) {
    console.warn('[contentStorage] save failed, falling back to localStorage', e.message);
    localStorage.setItem(`sh_ci_${type}`, JSON.stringify(items));
  }
}

export async function loadContent(type) {
  try {
    const res = await fetch(`${API}/api/content?type=${type}`, { headers: getHeaders() });
    if (!res.ok) throw new Error('fetch failed');
    const data = await res.json();
    return data.items || [];
  } catch (e) {
    console.warn('[contentStorage] load failed, falling back to localStorage', e.message);
    try { return JSON.parse(localStorage.getItem(`sh_ci_${type}`) || '[]'); }
    catch { return []; }
  }
}

export async function runCleanup() {
  try {
    await fetch(`${API}/api/content/cleanup`, { method: 'DELETE', headers: getHeaders() });
  } catch (e) {
    console.warn('[contentStorage] cleanup failed', e.message);
  }
}

// Returns { ok, status } so callers can tell the user when a save was refused
// (a 403 here used to vanish: fetch only throws on network errors).
export async function saveCard(id, ideaId, platId, content) {
  try {
    const res = await fetch(`${API}/api/content/cards`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ id, ideaId, platId, content }),
    });
    if (!res.ok) console.warn('[contentStorage] saveCard rejected, HTTP', res.status);
    return { ok: res.ok, status: res.status };
  } catch (e) {
    console.warn('[contentStorage] saveCard failed', e.message);
    return { ok: false, status: 'network' };
  }
}

// Sprint 6 (revised): archive helpers — idea status PATCH, card fetch, archive write
export async function patchIdea(id, updates) {
  try {
    const res = await fetch(`${API}/api/content/${id}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(updates),
    });
    return await res.json();
  } catch (e) {
    console.warn('[contentStorage] patchIdea failed', e.message);
    return { success: false, error: e.message };
  }
}

export async function fetchCards(ideaId) {
  try {
    const res = await fetch(`${API}/api/content/cards?ideaId=${ideaId}`, { headers: getHeaders() });
    const data = await res.json();
    return data.cards || [];
  } catch (e) {
    console.warn('[contentStorage] fetchCards failed', e.message);
    return [];
  }
}

export async function archiveIdea(ideaId, ideaData, cardsData, mediaUrls = []) {
  try {
    const res = await fetch(`${API}/api/archive`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ ideaId, ideaData, cardsData, mediaUrls }),
    });
    return await res.json();
  } catch (e) {
    console.warn('[contentStorage] archiveIdea failed', e.message);
    return { success: false, error: e.message };
  }
}

// Sprint 7.4: Restore to Active — deletes the archived_content row in Postgres.
// Takes the archive record's own id (archiveId), NOT the original idea id.
export async function deleteArchive(archiveId) {
  try {
    const res = await fetch(`${API}/api/archive/${archiveId}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return await res.json();
  } catch (e) {
    console.warn('[contentStorage] deleteArchive failed', e.message);
    return { success: false, error: e.message };
  }
}

// Sprint 7.2: cross-device research timestamps (lastRun/nextRun), stored
// server-side so they survive a browser switch or phone/desktop split.
export async function saveMeta(updates) {
  try {
    const res = await fetch(`${API}/api/content/meta`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(updates),
    });
    return await res.json();
  } catch (e) {
    console.warn('[contentStorage] saveMeta failed', e.message);
    return { success: false, error: e.message };
  }
}

export async function loadMeta() {
  try {
    const res = await fetch(`${API}/api/content/meta`, { headers: getHeaders() });
    const data = await res.json();
    return data.meta || {};
  } catch (e) {
    console.warn('[contentStorage] loadMeta failed', e.message);
    return {};
  }
}

export async function loadCards(ideaId) {
  try {
    const res = await fetch(`${API}/api/content/cards?ideaId=${ideaId}`, { headers: getHeaders() });
    if (!res.ok) throw new Error('fetch failed');
    const data = await res.json();
    const map = {};
    for (const row of data.cards || []) {
      map[`${row.idea_id}_${row.plat_id}`] = { content: row.content };
    }
    return map;
  } catch (e) {
    console.warn('[contentStorage] loadCards failed', e.message);
    return {};
  }
}